import { useState } from 'react';
import { X } from 'lucide-react';
import { useBaby } from '@/contexts/BabyContext';
import { addWaterLog } from '@/lib/water-firestore';

interface WaterLogSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const PRESETS = [50, 100, 150, 200];

const WaterLogSheet = ({ isOpen, onClose, onSaved }: WaterLogSheetProps) => {
  const { activeBaby } = useBaby();
  const [amount, setAmount] = useState<number | ''>(100);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const numAmount = typeof amount === 'number' ? amount : 0;
  const canSave = numAmount > 0 && numAmount <= 1000;

  const handleSave = async () => {
    if (!canSave || !activeBaby?.id || saving) return;
    setSaving(true);
    try {
      await addWaterLog(activeBaby.id, numAmount);
      onSaved?.();
      onClose();
      setAmount(100);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-3xl p-5 pb-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'hsl(var(--foreground))' }}>
            💧 Log water
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full"
            style={{ background: 'hsl(var(--muted) / 0.6)' }}
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Preset amounts */}
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Quick add</p>
        <div className="flex gap-2 mb-4">
          {PRESETS.map(ml => (
            <button
              key={ml}
              onClick={() => setAmount(ml)}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
              style={
                amount === ml
                  ? { background: 'hsl(var(--medicine-accent))', color: '#fff' }
                  : { background: 'hsl(var(--muted) / 0.4)', color: 'hsl(var(--muted-foreground))' }
              }
            >
              {ml}ml
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Or enter amount</p>
        <div className="flex items-center gap-2 mb-5">
          <input
            type="number"
            min={1}
            max={1000}
            value={amount}
            onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            className="flex-1 rounded-xl px-3 py-2.5 text-sm text-center font-semibold outline-none"
            style={{
              border: '1.5px solid hsl(var(--border))',
              background: 'hsl(var(--muted) / 0.2)',
              color: 'hsl(var(--foreground))',
            }}
          />
          <span className="text-sm font-semibold text-muted-foreground">ml</span>
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={!canSave || saving}
          className="w-full py-3.5 rounded-2xl text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-40"
          style={{ background: 'hsl(var(--medicine-accent))' }}
        >
          {saving ? 'Saving...' : `Log ${numAmount > 0 ? numAmount + 'ml' : ''}`}
        </button>

      </div>
    </div>
  );
};

export default WaterLogSheet;
